import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Card, Table, Pagination, Btn, Modal, Input, StatusBadge, InfoAlert,
         PageHeader, StatsGrid, StatCard, FilterBar, FilterSelect, DetailRow } from '../components/UI';
import { IconRefresh, IconCheck, IconWallet, IconTx, IconX } from '../components/Icons';
import { fmt } from '../utils/helpers';

export default function Transactions() {
  const [txns, setTxns]       = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [filters, setFilters] = useState({ type:'all', status:'all' });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal]     = useState(null);
  const [note, setNote]       = useState('');
  const [busy, setBusy]       = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getTransactions({ page, limit:20, ...filters });
      setTxns(r.transactions||[]); setTotal(r.total||0); setSummary(r.summary||{});
    } catch(e) { toast(e.message,'error'); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(()=>{ load(); },[load]);

  const setFilter = (k,v) => { setFilters(f=>({...f,[k]:v})); setPage(1); };

  const handleRetry = async (tx) => {
    try { const r = await api.retryTransaction(tx.id); toast(r.message,'success'); load(); }
    catch(e) { toast(e.message,'error'); }
  };

  const handleResolve = async () => {
    setBusy(true);
    try { await api.resolveTransaction(selected.id, note); toast('Resolved & wallet credited','success'); setModal(null); load(); }
    catch(e) { toast(e.message,'error'); }
    finally { setBusy(false); }
  };

  const columns = [
    { key:'user', label:'User', render: tx=>(
      <div><div style={{ fontSize:13, fontWeight:500 }}>{tx.User?.name||'—'}</div><div style={{ fontSize:11, color:'var(--text3)' }}>{tx.User?.email}</div></div>
    )},
    { key:'amount', label:'Amount', render: tx=>(
      <span style={{ fontFamily:'Geist Mono,monospace', fontWeight:700, fontSize:13, color: tx.type==='topup'?'var(--green)':'var(--text2)', whiteSpace:'nowrap' }}>
        {tx.type==='topup'?'+':'−'}{fmt.naira(tx.amount)}
      </span>
    )},
    { key:'type',   label:'Type',   render: tx=><span style={{ fontSize:11, padding:'2px 8px', borderRadius:99, background: tx.type==='topup'?'var(--accent-dim)':'var(--purple-dim)', color: tx.type==='topup'?'var(--accent)':'var(--purple)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>{tx.type}</span> },
    { key:'status', label:'Status', render: tx=><StatusBadge status={tx.status} /> },
    { key:'desc',   label:'Description', tdStyle:{ maxWidth:180 }, render: tx=><span style={{ fontSize:12, color:'var(--text2)' }}>{tx.description}</span> },
    { key:'user',   label:'User', tdStyle:{ maxWidth:180 }, render: tx=><span style={{ fontSize:12, color:'var(--text2)' }}>{tx.user?.email||'—'}</span> },
    { key:'ref',    label:'Reference', render: tx=><code style={{ fontSize:10, color:'var(--text3)', display:'block', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx.reference}</code> },
    { key:'date',   label:'Date', render: tx=><span style={{ fontSize:11, color:'var(--text3)', whiteSpace:'nowrap' }}>{fmt.datetime(tx.createdAt)}</span> },
    { key:'actions',label:'Actions', render: tx=>(
      <div style={{ display:'flex', gap:5 }}>
        {tx.status!=='success' && tx.type==='topup' && <Btn size="xs" variant="subtle" icon={IconRefresh} onClick={()=>handleRetry(tx)}>Retry</Btn>}
        {tx.status==='failed' && <Btn size="xs" variant="subtle" icon={IconCheck} style={{ color:'var(--green)' }} onClick={()=>{ setSelected(tx); setNote(''); setModal('resolve'); }}>Resolve</Btn>}
      </div>
    )},
  ];

  const mobileCard = (tx) => (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div>
          <div style={{ fontWeight:600, fontSize:13 }}>{tx.User?.name||'—'}</div>
          <div style={{ fontSize:11, color:'var(--text3)' }}>{fmt.datetime(tx.createdAt)}</div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontFamily:'Geist Mono,monospace', fontWeight:700, color: tx.type==='topup'?'var(--green)':'var(--text2)' }}>
            {tx.type==='topup'?'+':'−'}{fmt.naira(tx.amount)}
          </div>
          <StatusBadge status={tx.status} />
        </div>
      </div>
      <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>{tx.description}</div>
      <div style={{ fontSize:11, color:'var(--text3)', marginBottom:8 }}>{tx.user?.email||'—'}</div>
      <div style={{ display:'flex', gap:6 }}>
        {tx.status!=='success' && tx.type==='topup' && <Btn size="xs" variant="subtle" icon={IconRefresh} onClick={()=>handleRetry(tx)}>Retry</Btn>}
        {tx.status==='failed' && <Btn size="xs" variant="subtle" icon={IconCheck} style={{ color:'var(--green)' }} onClick={()=>{ setSelected(tx); setNote(''); setModal('resolve'); }}>Resolve</Btn>}
      </div>
    </>
  );

  return (
    <div className="page-enter">
      <PageHeader title="Transactions" subtitle="All payment activity across the platform" />

      <StatsGrid>
        <StatCard label="Total Revenue" value={fmt.naira(summary.total_topup)} icon={IconWallet} accent="var(--green)" />
        <StatCard label="Total Debits"  value={fmt.naira(summary.total_debit)} icon={IconTx}    accent="var(--accent)" />
        <StatCard label="Failed"  value={summary.failed_count||0}  icon={IconX}       accent="var(--red)" />
        <StatCard label="Pending" value={summary.pending_count||0} icon={IconRefresh} accent="var(--yellow)" />
      </StatsGrid>

      <FilterBar>
        <FilterSelect value={filters.type} onChange={v=>setFilter('type',v)}
          options={[{value:'all',label:'All Types'},{value:'topup',label:'Top-up'},{value:'debit',label:'Debit'}]} />
        <FilterSelect value={filters.status} onChange={v=>setFilter('status',v)}
          options={[{value:'all',label:'All Status'},{value:'success',label:'Success'},{value:'failed',label:'Failed'},{value:'pending',label:'Pending'}]} />
        <Btn size="sm" variant="ghost" icon={IconRefresh} onClick={load}>Refresh</Btn>
      </FilterBar>

      <Card style={{ padding:0, overflow:'hidden' }}>
        <Table columns={columns} rows={txns} loading={loading} emptyText="No transactions found." mobileCard={mobileCard} />
        <div style={{ borderTop:'1px solid var(--border)', padding:'0 14px' }}>
          <Pagination page={page} total={total} limit={20} onChange={setPage} />
        </div>
      </Card>

      {modal==='resolve' && selected && (
        <Modal title="Resolve Failed Transaction" onClose={()=>setModal(null)}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ background:'var(--surface2)', borderRadius:10, padding:14 }}>
              <DetailRow label="User"   value={selected.User?.name} />
              <DetailRow label="Email"  value={selected.User?.email} />
              <DetailRow label="Amount" value={<b style={{ color:'var(--green)' }}>{fmt.naira(selected.amount)}</b>} />
              <DetailRow label="Ref"    value={<code style={{ fontSize:11 }}>{selected.reference}</code>} />
            </div>
            <InfoAlert type="success">✅ This will credit <b>{fmt.naira(selected.amount)}</b> to the user's wallet and mark the transaction as success.</InfoAlert>
            <Input label="Admin Note (optional)" value={note} onChange={setNote} placeholder="e.g. Verified via bank statement" />
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
              <Btn variant="success" icon={IconCheck} onClick={handleResolve} loading={busy}>Confirm & Credit</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}